import type {
  TrainingEvalMetricsView,
  TrainingEvalRunSummaryView,
  TrainingEvalRunView,
  TrainingEvalScenarioView,
  TrainingEvalSuccessTermsView,
} from "@/api/trainingEval";
import type { JsonObject } from "@/api/common";
import { runDurationSeconds } from "@/modules/evaluation/public";
import { parseUtcMs } from "@/shared/utils/datetime/public";
import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import { formatRelativeTime } from "@/utils/datetime";
import { boxStats, scalarTermValues, type BoxStats } from "../evalDistributionModel";
import { evalRunPolicyLabel } from "../evalRunFormat";
import {
  metricValue,
  outcomeBreakdownFromEpisodes,
  SUCCESS_RATE_METRIC_KEYS,
  type OutcomeDatum,
} from "../evalRunResultModel";
import { simulationSeed } from "../evalRunScenarioModel";

export const MERGED_SERIES_KEY = "merged";
export const BASELINE_SERIES_KEY = "baseline";
const GROUP_SERIES_KEY_PREFIX = "group:";

export function groupSeriesKey(key: string): string {
  return `${GROUP_SERIES_KEY_PREFIX}${key}`;
}

export function isPooledSeriesKey(key: string): boolean {
  return (
    key === BASELINE_SERIES_KEY ||
    key === MERGED_SERIES_KEY ||
    key.startsWith(GROUP_SERIES_KEY_PREFIX)
  );
}

// 값의 column 순서와 별개로 사용자 term 순서를 유지한다.
export function userSeriesFirst(items: readonly EvalResultSeries[]): EvalResultSeries[] {
  return [
    ...items.filter((item) => item.key !== BASELINE_SERIES_KEY),
    ...items.filter((item) => item.key === BASELINE_SERIES_KEY),
  ];
}

export function shortRunName(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

export type EvalCompareDistribution = {
  key: string;
  stats: BoxStats;
};

export type EvalCompareSummary = {
  finalSuccessRate: number | null;
  durationSec: number | null;
  outcomeBreakdown: OutcomeDatum[];
  distributions: EvalCompareDistribution[];
};

export type EvalCompareRecord = {
  id: string;
  summaryRow: TrainingEvalRunSummaryView;
  detail: TrainingEvalRunView | null;
  metrics: TrainingEvalMetricsView | null;
  terms: TrainingEvalSuccessTermsView | null;
  scenario: TrainingEvalScenarioView | null;
  policyName: string;
  startedAt: number | null;
  params: JsonObject;
  summary: EvalCompareSummary;
};

export type EvalResultSeries = {
  key: string;
  summary: EvalCompareSummary;
  color: string;
  label: string;
  shortLabel: string;
  sublabel: string;
  // policy 를 학습한 config 이름이다. 여러 run 을 합친 열은 서로 다른 값을 ", " 로 잇는다. 모르면
  // 생략하거나 null 이다. 표의 열 머리가 모든 열에서 같은 자리에 이 값을 보인다.
  config?: string | null;
  baseline?: {
    hash: string | null;
    seedCount: number;
    episodeCount: number;
    sessionId: string;
    sessionName: string | null;
    // 선택한 config 의 결과가 없어 대신 조회한 config 다. 대체가 아니면 null.
    fallbackConfig: string | null;
  };
};

// 열 머리와 상세 카드가 같은 자리에 보일 config 문구다. 값을 모르면 null 이다.
export function configLine(config: string | null | undefined): string | null {
  return config === null || config === undefined ? null : `${config} config`;
}

// 여러 run 을 합친 열의 값이다(config·Seed). 서로 다른 값은 처음 나온 순서대로 ", " 로 잇는다.
// 하나도 모르면 null 이다.
export function joinDistinct(values: readonly (string | null | undefined)[]): string | null {
  const known = [
    ...new Set(values.filter((value): value is string => value !== null && value !== undefined)),
  ];
  return known.length === 0 ? null : known.join(", ");
}

export type CompareRun = EvalResultSeries & {
  run: EvalCompareRecord;
  simulationSeed: string | null;
};

/** 여러 run의 scalar term 원시 값을 이름별 box 통계로 합친다. */
export function scalarCompareDistributions(
  termViews: readonly (TrainingEvalSuccessTermsView | null)[],
): EvalCompareDistribution[] {
  const valuesByName = new Map<string, number[]>();
  for (const terms of termViews) {
    for (const term of terms?.terms ?? []) {
      if (term.kind !== "scalar") continue;
      const values = valuesByName.get(term.name) ?? [];
      values.push(...scalarTermValues(term));
      valuesByName.set(term.name, values);
    }
  }
  return [...valuesByName]
    .filter(([, values]) => values.length > 0)
    .map(([key, values]) => ({ key, stats: boxStats(values) }));
}

export function buildCompareRecord(args: {
  summary: TrainingEvalRunSummaryView;
  detail: TrainingEvalRunView | null;
  metrics: TrainingEvalMetricsView | null;
  terms: TrainingEvalSuccessTermsView | null;
  scenario: TrainingEvalScenarioView | null;
}): EvalCompareRecord {
  const { summary, detail, metrics, terms, scenario } = args;
  const params: JsonObject = { ...(detail?.params ?? {}), ...(detail?.override_params ?? {}) };
  const startedAt = parseUtcMs(
    metrics?.started_at ?? detail?.started_at ?? summary.started_at ?? summary.created_at,
  );
  const successRate = metricValue(metrics, SUCCESS_RATE_METRIC_KEYS) ?? summary.success_rate;
  return {
    id: summary.id,
    summaryRow: summary,
    detail,
    metrics,
    terms,
    scenario,
    policyName: evalRunPolicyLabel(summary),
    startedAt,
    params,
    summary: {
      finalSuccessRate: successRate,
      durationSec: runDurationSeconds(summary),
      outcomeBreakdown: outcomeBreakdownFromEpisodes(terms?.episodes ?? []),
      distributions: scalarCompareDistributions([terms]),
    },
  };
}

export function buildCompareRuns(
  records: readonly EvalCompareRecord[],
  slotById: Readonly<Record<string, number>>,
  nowMs: number = Date.now(),
): CompareRun[] {
  return records
    .map((run) => {
      const slot = slotById[run.id] ?? 0;
      const seed = simulationSeed(run.params);
      return {
        key: run.id,
        summary: run.summary,
        run,
        simulationSeed: seed,
        color: CHART_SERIES_PALETTE[slot % CHART_SERIES_PALETTE.length],
        label: run.policyName,
        shortLabel: shortRunName(run.id),
        sublabel: `Seed ${seed ?? "-"} · ${formatRelativeTime(run.startedAt, nowMs)}`,
        config: run.summaryRow.config_name ?? null,
      };
    })
    .sort((left, right) => (slotById[left.key] ?? 0) - (slotById[right.key] ?? 0));
}
